import React, { useState } from 'react';
import { useQuery, useMutation } from '@apollo/client';
import { Card, Table, Button, Space, Tag, Tabs, message, Popconfirm, Input } from 'antd';
import { PlusOutlined, EditOutlined, DeleteOutlined, SearchOutlined } from '@ant-design/icons';
import { useNavigate } from 'react-router-dom';
import { getAllExploreCategories, getAllExploreAudios } from '../../graphql/queries';
import { deleteExploreCategory, deleteExploreAudio } from '../../graphql/mutation';
import './Explore.css';

const { TabPane } = Tabs;

const Explore = () => {
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState('categories');
  const [categorySearch, setCategorySearch] = useState('');
  const [audioSearch, setAudioSearch] = useState('');

  // Queries
  const { loading: categoriesLoading, data: categoriesData, refetch: refetchCategories } = useQuery(getAllExploreCategories);
  const { loading: audiosLoading, data: audiosData, refetch: refetchAudios } = useQuery(getAllExploreAudios);

  // Mutations
  const [deleteCategoryMutation] = useMutation(deleteExploreCategory, {
    onCompleted: () => {
      message.success('Category deleted successfully');
      refetchCategories();
    },
    onError: (error) => message.error(error.message)
  });

  const [deleteAudioMutation] = useMutation(deleteExploreAudio, {
    onCompleted: () => {
      message.success('Audio deleted successfully');
      refetchAudios();
    },
    onError: (error) => message.error(error.message)
  });

  // Data
  const categories = categoriesData?.getAllExploreCategories || [];
  const audios = audiosData?.getAllExploreAudios || [];

  // Filtered data
  const filteredCategories = categories.filter(cat =>
    cat.name?.toLowerCase().includes(categorySearch.toLowerCase())
  );

  const filteredAudios = audios.filter(audio =>
    audio.name?.toLowerCase().includes(audioSearch.toLowerCase())
  );

  // Get category name by ID
  const getCategoryName = (categoryId) => {
    const category = categories.find(c => c.id === categoryId);
    return category?.name || '-';
  };

  // Category columns
  const categoryColumns = [
    {
      title: 'Name',
      dataIndex: 'name',
      key: 'name',
      sorter: (a, b) => a.name.localeCompare(b.name),
    },
    {
      title: 'Order',
      dataIndex: 'order',
      key: 'order',
      sorter: (a, b) => a.order - b.order,
      width: 100,
    },
    {
      title: 'Audio Count',
      key: 'audioCount',
      render: (_, record) => record.audios?.length || 0,
      width: 120,
    },
    {
      title: 'Status',
      dataIndex: 'isActive',
      key: 'isActive',
      render: (isActive) => (
        <Tag color={isActive ? 'success' : 'default'}>
          {isActive ? 'Active' : 'Inactive'}
        </Tag>
      ),
      width: 100,
    },
    {
      title: 'Actions',
      key: 'actions',
      width: 150,
      render: (_, record) => (
        <Space>
          <Button
            type="primary"
            icon={<EditOutlined />}
            size="small"
            onClick={() => navigate(`/explore/categories/${record.id}`)}
          />
          <Popconfirm
            title="Delete Category"
            description="Are you sure you want to delete this category?"
            onConfirm={() => deleteCategoryMutation({ variables: { id: record.id } })}
            okText="Yes"
            cancelText="No"
          >
            <Button
              danger
              icon={<DeleteOutlined />}
              size="small"
            />
          </Popconfirm>
        </Space>
      ),
    },
  ];

  // Audio columns
  const audioColumns = [
    {
      title: 'Image',
      dataIndex: 'imageUrl',
      key: 'imageUrl',
      width: 80,
      render: (url) => url ? (
        <img src={url} alt="Audio" style={{ width: 50, height: 50, borderRadius: 8, objectFit: 'cover' }} />
      ) : (
        <div style={{ width: 50, height: 50, borderRadius: 8, background: '#f0f0f0' }} />
      ),
    },
    {
      title: 'Name',
      dataIndex: 'name',
      key: 'name',
      sorter: (a, b) => a.name.localeCompare(b.name),
    },
    {
      title: 'Category',
      dataIndex: 'categoryId',
      key: 'categoryId',
      render: (categoryId) => getCategoryName(categoryId),
    },
    {
      title: 'Order',
      dataIndex: 'order',
      key: 'order',
      sorter: (a, b) => a.order - b.order,
      width: 100,
    },
    {
      title: 'Status',
      dataIndex: 'isActive',
      key: 'isActive',
      render: (isActive) => (
        <Tag color={isActive ? 'success' : 'default'}>
          {isActive ? 'Active' : 'Inactive'}
        </Tag>
      ),
      width: 100,
    },
    {
      title: 'Actions',
      key: 'actions',
      width: 150,
      render: (_, record) => (
        <Space>
          <Button
            type="primary"
            icon={<EditOutlined />}
            size="small"
            onClick={() => navigate(`/explore/audios/${record.id}`)}
          />
          <Popconfirm
            title="Delete Audio"
            description="Are you sure you want to delete this audio?"
            onConfirm={() => deleteAudioMutation({ variables: { id: record.id } })}
            okText="Yes"
            cancelText="No"
          >
            <Button
              danger
              icon={<DeleteOutlined />}
              size="small"
            />
          </Popconfirm>
        </Space>
      ),
    },
  ];

  return (
    <div className="explore-container">
      <div className="page-header">
        <h1 className="page-title">Explore Management</h1>
        <p className="page-subtitle">Manage categories and featured audios</p>
      </div>

      <Card className="explore-card">
        <Tabs activeKey={activeTab} onChange={setActiveTab}>
          <TabPane tab="Categories" key="categories">
            <div className="table-header">
              <Input
                placeholder="Search categories..."
                prefix={<SearchOutlined />}
                value={categorySearch}
                onChange={(e) => setCategorySearch(e.target.value)}
                style={{ width: 300 }}
              />
              <Button
                type="primary"
                icon={<PlusOutlined />}
                onClick={() => navigate('/explore/categories/create')}
              >
                Add Category
              </Button>
            </div>
            <Table
              columns={categoryColumns}
              dataSource={filteredCategories}
              loading={categoriesLoading}
              rowKey="id"
              pagination={{ pageSize: 10 }}
            />
          </TabPane>

          <TabPane tab="Audios" key="audios">
            <div className="table-header">
              <Input
                placeholder="Search audios..."
                prefix={<SearchOutlined />}
                value={audioSearch}
                onChange={(e) => setAudioSearch(e.target.value)}
                style={{ width: 300 }}
              />
              <Button
                type="primary"
                icon={<PlusOutlined />}
                onClick={() => navigate('/explore/audios/create')}
              >
                Add Audio
              </Button>
            </div>
            <Table
              columns={audioColumns}
              dataSource={filteredAudios}
              loading={audiosLoading}
              rowKey="id"
              pagination={{ pageSize: 10 }}
            />
          </TabPane>
        </Tabs>
      </Card>
    </div>
  );
};

export default Explore;
